import { FloorRoom, RoomPoint } from '../interface/floor-room';

/**
 * Client-side polygon helpers for room boundaries (floor plan image pixel space).
 * The API is the source of truth for room assignment; these power live previews only.
 */

/** Ray casting point-in-polygon; points on an edge count as inside. */
export function polygonContains(polygon: readonly RoomPoint[], point: RoomPoint): boolean {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];

    if (distanceToSegment(point, a, b) < 1e-6) {
      return true;
    }

    if ((a.y > point.y) !== (b.y > point.y)) {
      const xAtY = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
      if (point.x < xAtY) {
        inside = !inside;
      }
    }
  }

  return inside;
}

/** Room containing the point; rooms never overlap, so the first (oldest) match wins ties on shared walls. */
export function findRoomAt(rooms: readonly FloorRoom[], point: RoomPoint | null | undefined): FloorRoom | null {
  if (!point) {
    return null;
  }
  return rooms.find(r => r.points.length >= 3 && polygonContains(r.points, point)) ?? null;
}

export function polygonArea(polygon: readonly RoomPoint[]): number {
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum / 2);
}

export function toSvgPoints(polygon: readonly RoomPoint[]): string {
  return polygon.map(p => `${p.x},${p.y}`).join(' ');
}

/** A point inside the polygon suitable for a label (area centroid, with fallbacks for concave shapes). */
export function labelPoint(polygon: readonly RoomPoint[]): RoomPoint {
  const centroid = areaCentroid(polygon);
  if (centroid && polygonContains(polygon, centroid)) {
    return centroid;
  }

  // Concave fallback: centroid of the first triangle fan that lies inside the shape
  for (let i = 1; i < polygon.length - 1; i++) {
    const candidate = {
      x: (polygon[0].x + polygon[i].x + polygon[i + 1].x) / 3,
      y: (polygon[0].y + polygon[i].y + polygon[i + 1].y) / 3
    };
    if (polygonContains(polygon, candidate)) {
      return candidate;
    }
  }

  return polygon[0];
}

export interface SnapResult {
  point: RoomPoint;
  snapped: boolean;
}

/**
 * Snaps to the nearest vertex within `radius`, otherwise to the nearest edge within `radius`,
 * so adjacent rooms can share exact walls (overlaps are rejected by the API).
 */
export function snapToRooms(
  point: RoomPoint,
  rooms: readonly Pick<FloorRoom, 'id' | 'points'>[],
  radius: number,
  excludeRoomId?: string | null
): SnapResult {
  let best: RoomPoint | null = null;
  let bestDistance = radius;

  for (const room of rooms) {
    if (room.id === excludeRoomId) continue;
    for (const vertex of room.points) {
      const d = distance(point, vertex);
      if (d <= bestDistance) {
        best = vertex;
        bestDistance = d;
      }
    }
  }

  if (best) {
    return { point: { ...best }, snapped: true };
  }

  bestDistance = radius;
  for (const room of rooms) {
    if (room.id === excludeRoomId) continue;
    for (let i = 0; i < room.points.length; i++) {
      const a = room.points[i];
      const b = room.points[(i + 1) % room.points.length];
      const projected = projectOntoSegment(point, a, b);
      const d = distance(point, projected);
      if (d <= bestDistance) {
        best = projected;
        bestDistance = d;
      }
    }
  }

  return best ? { point: roundPoint(best), snapped: true } : { point, snapped: false };
}

export function clampToBounds(point: RoomPoint, width: number, height: number): RoomPoint {
  return {
    x: Math.min(Math.max(point.x, 0), width),
    y: Math.min(Math.max(point.y, 0), height)
  };
}

export function roundPoint(point: RoomPoint): RoomPoint {
  return { x: Math.round(point.x * 100) / 100, y: Math.round(point.y * 100) / 100 };
}

export function distance(a: RoomPoint, b: RoomPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function areaCentroid(polygon: readonly RoomPoint[]): RoomPoint | null {
  let area = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const cross = a.x * b.y - b.x * a.y;
    area += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }

  if (Math.abs(area) < 1e-9) {
    return null;
  }

  area /= 2;
  return { x: cx / (6 * area), y: cy / (6 * area) };
}

function projectOntoSegment(p: RoomPoint, a: RoomPoint, b: RoomPoint): RoomPoint {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return a;
  }
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

function distanceToSegment(p: RoomPoint, a: RoomPoint, b: RoomPoint): number {
  return distance(p, projectOntoSegment(p, a, b));
}

/**
 * Room for a label centred on `point`: twice the distance to the nearest edge along the
 * horizontal (or vertical) line through it. Works for concave shapes (L-shaped rooms).
 * Returns 0 when the point is not inside the polygon.
 */
export function centeredSpan(
  polygon: readonly RoomPoint[],
  point: RoomPoint,
  direction: 'horizontal' | 'vertical'
): number {
  const horizontal = direction === 'horizontal';
  const line = horizontal ? point.y : point.x;
  const along = horizontal ? point.x : point.y;
  const hits: number[] = [];

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const aLine = horizontal ? a.y : a.x;
    const bLine = horizontal ? b.y : b.x;

    if ((aLine > line) !== (bLine > line)) {
      const aAlong = horizontal ? a.x : a.y;
      const bAlong = horizontal ? b.x : b.y;
      hits.push(aAlong + ((line - aLine) * (bAlong - aAlong)) / (bLine - aLine));
    }
  }

  hits.sort((m, n) => m - n);
  for (let k = 0; k + 1 < hits.length; k += 2) {
    if (along >= hits[k] && along <= hits[k + 1]) {
      return 2 * Math.min(along - hits[k], hits[k + 1] - along);
    }
  }
  return 0;
}
